import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Grid,
  Link as MuiLink,
  Container,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useAnalytics } from '../../hooks/useAnalytics';

const LoginForm: React.FC = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema: Yup.object({
      username: Yup.string().email('Dirección de correo electrónico inválida').required('Requerido'),
      password: Yup.string().required('Requerido'),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      analytics.trackFormStart('login');
      try {
        await login(values);
        
        // Track successful login after user is authenticated
        if (user) {
          const userType = user.is_admin ? 'admin' : user.is_buyer ? 'buyer' : 'seller';
          analytics.trackUserLogin(userType);
        }
        analytics.trackFormSubmit('login', true);
        
        navigate('/dashboard');
      } catch (err: any) {
        // Make sure error is a string
        const errorMessage = err.response?.data?.detail
          ? typeof err.response.data.detail === 'string'
            ? err.response.data.detail
            : JSON.stringify(err.response.data.detail)
          : 'Error al iniciar sesión. Por favor verifique sus credenciales.';

        setError(errorMessage);
        analytics.trackFormSubmit('login', false);
        analytics.trackError(errorMessage, 'login_error');
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Iniciar Sesión
        </Typography>
        <Typography variant="body1" gutterBottom align="center" sx={{ mb: 4 }}>
          Accede a tu cuenta de Facto
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit}>
          <TextField
            fullWidth
            id="username"
            name="username"
            label="Correo Electrónico"
            variant="outlined"
            margin="normal"
            value={formik.values.username}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.username && Boolean(formik.errors.username)}
            helperText={formik.touched.username && formik.errors.username}
          />
          <TextField
            fullWidth
            id="password"
            name="password"
            label="Contraseña"
            type="password"
            variant="outlined"
            margin="normal"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Iniciar Sesión'}
          </Button>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <MuiLink component={Link} to="/forgot-password" variant="body2">
                ¿Olvidaste tu contraseña?
              </MuiLink>
            </Grid>
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <MuiLink component={Link} to="/register" variant="body2">
                ¿No tienes una cuenta? Regístrate
              </MuiLink>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default LoginForm;
